import React from "react";
import { useNavigate } from "react-router";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { put } from "@common/api/apiShared.ts";
import { useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";
import { useTranslation } from "@localization/useTranslation.ts";
import { useUrlProvider } from "../../../useUrlProvider.ts";
import ExcludedUsersField from "../ExcludedUsersField.tsx";
import type { IncidentLogFieldModel } from "../../IncidentLogFieldRenderers.tsx";

export interface IncidentLogExcludedUsersFieldProps {
    incidentLogId: string;
    incidentLog: IncidentLogFieldModel | undefined;
    isLoading: boolean;
    onChange: (changeFunc: (incidentLog: IncidentLogFieldModel) => void) => Promise<void>;
    disabled: boolean;
    isPreview: boolean;
    errorText?: string;
}

function IncidentLogExcludedUsersField(props: IncidentLogExcludedUsersFieldProps) {
    const snackbar = useDotLegalSnackbar();
    const { translateString } = useTranslation();
    const navigate = useNavigate();
    const urlProvider = useUrlProvider();

    // TODO: The backend has no incident log user access endpoint yet. The template editor preview never calls it, but
    // removing an excluded user from a real incident needs that endpoint first.
    const removeFromExcludedMutation = useDotLegalMutation((userId: string) =>
        put(`/incident-log-user-access/${props.incidentLogId}/remove-excluded/${userId}`, null)
    );

    async function onRemoveFromExcluded(userId: string) {
        await removeFromExcludedMutation.mutateAsync(userId, {
            onSuccess: () => {
                snackbar.show(translateString("removedFromExcluded"));
            },
        });
    }

    return (
        <ExcludedUsersField
            entity={props.incidentLog}
            isLoading={props.isLoading}
            onChange={props.onChange}
            onConfirmNavigate={() => navigate(urlProvider.getIncidentLogUrl())}
            onRemoveFromExcluded={onRemoveFromExcluded}
            removeFromExcludedInProgress={removeFromExcludedMutation.isLoading}
            disabled={props.disabled}
            isPreview={props.isPreview}
            errorText={props.errorText}
            toolTipText={translateString("incidentLogExcludedUsersTooltip")}
            warningText={translateString("incidentLogExcludedUsersWarning")}
        />
    );
}

export default IncidentLogExcludedUsersField;
